import { useAsync2 } from "nate-react-api-helpers";
import { MouseEvent, useState } from "react";
import { api } from "../../api/API";
import { Goal, Project } from "../../api/Projects";
import { Task } from "../../api/Tasks";
import { SidePanel } from "../../misc/SidePanel";
import { useUrlState } from "../../misc/urlParams";
import { GoalPanel } from "../project/GoalPanel";
import { ProjectPanel } from "../project/ProjectPanel";
import { TrailStep } from "../task/panel/TaskCrumbs";
import { TaskPanelStack } from "../task/panel/TaskPanelStack";
import { UnscheduledPanel } from "../task/UnscheduledPanel";

// usePanels owns the side panel a page shows: whichever of a task, a goal and a
// project the url names, the trail between them, and the openers the page hands its
// own rows. Holding it in one place is what lets a task lead to the project it's
// filed under, and that project lead back to the task, on every page alike.
//
// The url is what says which is open, so a link opens the same thing and the back
// button walks out of it. A task that hasn't been saved yet has no id to name it by,
// so the url says "new" instead (see useOpenTask). A project that hasn't been saved
// yet is held here until it has an id.
export function usePanels(props: {
    // the tasks and projects the page already has on screen, so opening one of them
    // costs no fetch
    tasks?: Task[];
    projects?: Project[];

    // unscheduled is what the unscheduled panel lists and does, on a page that offers it
    unscheduled?: {
        tasks: Task[];
        saving?: boolean;
        onContextMenu: (task: Task, e: MouseEvent) => void;
        onToggle: (task: Task, checked: boolean) => void;
        onDragStart: (task: Task) => void;
        onDragEnd: () => void;
    };
}) {
    const url = useUrlState();
    const taskId = url.id("task");
    const projectId = url.id("project");
    const goalId = url.id("goal");

    // fromTask is the task a project or goal was opened out of, which is where the
    // trail leads back to
    const fromTaskId = url.id("fromTask");

    // listPanel names a list shown in the panel, which a task opened out of leads back to
    const listPanel = url.text("panel");

    const newTask = url.text("task") === "new";
    const [newProject, setNewProject] = useState<Partial<Project> | null>(null);

    const tasks = props.tasks || [];
    const projects = props.projects || [];

    // the task panel fetches its own copy either way (see TaskPanelStack), so an id
    // is enough to open one
    const task: Partial<Task> | null = taskId
        ? (tasks.find(row => row.id === taskId) || { id: taskId })
        : (newTask ? {} : null);

    const listedProject = projects.find(row => row.id === projectId) || null;
    const fetchProject = useAsync2(async (input: { id: number }) =>
        input.id ? api.projects.get({ id: input.id }) : null,
        { id: listedProject ? 0 : projectId }, [listedProject ? 0 : projectId]);

    const listedGoal = projects
        .flatMap(row => row.goals)
        .find(goal => goal.id === goalId) || null;

    const fetchGoal = useAsync2(async (input: { id: number }) =>
        input.id ? api.projects.getGoal({ id: input.id }) : null,
        { id: listedGoal ? 0 : goalId }, [listedGoal ? 0 : goalId]);

    const project = projectId ? (listedProject || fetchProject.result) : newProject;
    const goal = goalId ? (listedGoal || fetchGoal.result) : null;

    // the step back to the task needs its title, and only the panel it was replaced
    // by is left to name it
    const listedFrom = tasks.find(row => row.id === fromTaskId) || null;
    const wantFrom = fromTaskId && fromTaskId !== taskId ? fromTaskId : 0;
    const fetchFrom = useAsync2(async (input: { id: number }) =>
        input.id ? api.tasks.get({ id: input.id }) : null,
        { id: listedFrom ? 0 : wantFrom }, [listedFrom ? 0 : wantFrom]);

    const fromTask = wantFrom ? (listedFrom || fetchFrom.result) : null;

    const clear = () => {
        setNewProject(null);
    };

    // a page's own rows start over, so opening one closes whatever was opened out of
    // the last one
    const openTask = (value: Partial<Task>) => {
        clear();
        url.set({ task: value.id ? String(value.id) : "new", project: "", goal: "", fromTask: "", panel: "" });
    };

    const openProject = (value: Partial<Project>) => {
        clear();
        if (!value.id) setNewProject(value);
        url.set({ task: "", project: value.id ? String(value.id) : "", goal: "", fromTask: "", panel: "" });
    };

    const openGoal = (value: Goal) => {
        clear();
        url.set({ task: "", project: "", goal: String(value.id), fromTask: "", panel: "" });
    };

    // the unscheduled list opens and closes from its tile
    const toggleUnscheduled = () => {
        clear();
        url.set({
            task: "", project: "", goal: "", fromTask: "",
            panel: listPanel === "unscheduled" ? "" : "unscheduled",
        });
    };

    const openUnscheduled = () => {
        clear();
        url.set({ task: "", project: "", goal: "", fromTask: "", panel: "unscheduled" });
    };

    // a task opened out of the unscheduled list keeps the list as the way back
    const openFromUnscheduled = (value: Task) => {
        clear();
        url.set({ task: String(value.id), project: "", goal: "", fromTask: "" });
    };

    // a task or goal opened out of a panel keeps what it was opened from, which is
    // what the trail back is read from
    const openTaskInside = (value: Task) => {
        clear();
        url.set({ task: String(value.id) });
    };

    const openGoalInside = (value: Goal) => {
        clear();
        url.set({ task: "", goal: String(value.id) });
    };

    // what a task is filed under takes the panel, and the task becomes the step back
    const openFiling = (ref: { task: number; project: number; goal: number }) => {
        clear();
        url.set({
            task: "",
            project: String(ref.project),
            goal: ref.goal ? String(ref.goal) : "",
            fromTask: ref.task ? String(ref.task) : "",
        });
    };

    const closeAll = () => {
        clear();
        url.set({ task: "", project: "", goal: "", fromTask: "", panel: "" });
    };

    // closing walks back out one step of the trail, and closes the panel once there
    // are none left
    const closeTo = (trail: TrailStep[]) => () => {
        const back = trail.length > 0 ? trail[trail.length - 1] : null;
        if (back && back.onClick) back.onClick();
        else closeAll();
    };

    const fromCrumb: TrailStep[] = fromTask
        ? [{ title: fromTask.title, onClick: () => openTask(fromTask) }]
        : [];

    const projectCrumb: TrailStep[] = projectId && project?.name
        ? [{ title: project.name, onClick: () => url.set({ task: "", goal: "" }) }]
        : [];

    const goalCrumb: TrailStep[] = goal
        ? [{ title: goal.name, onClick: () => url.set({ task: "" }) }]
        : [];

    const unscheduledOpen = listPanel === "unscheduled" && !!props.unscheduled;
    const unscheduledCrumb: TrailStep[] = unscheduledOpen && task && !projectId && !goalId
        ? [{ title: "Unscheduled", onClick: () => url.set({ task: "" }) }]
        : [];

    const taskTrail = unscheduledCrumb.concat(fromCrumb).concat(projectCrumb).concat(goalCrumb);
    const goalTrail = fromCrumb.concat(projectCrumb);

    const element = (
        <>
            {task && (
                <SidePanel>
                    <TaskPanelStack
                        key={task.id || "new"}
                        value={task}
                        ancestors={taskTrail}
                        onClose={closeTo(taskTrail)}
                        onOpenProject={openFiling}
                        onSaved={saved => {
                            // a new task becomes an existing one, which switches the
                            // panel over to saving as you go
                            if (!task.id) openTask(saved);
                        }}
                    />
                </SidePanel>
            )}

            {!task && goal && (
                <SidePanel>
                    <GoalPanel
                        key={goal.id}
                        value={goal}
                        ancestors={goalTrail}
                        onClose={closeTo(goalTrail)}
                        onOpenTask={openTaskInside}
                    />
                </SidePanel>
            )}

            {!task && !goal && !project && unscheduledOpen && props.unscheduled && (
                <SidePanel>
                    <UnscheduledPanel
                        tasks={props.unscheduled.tasks}
                        saving={props.unscheduled.saving}
                        onClose={closeAll}
                        onOpen={openFromUnscheduled}
                        onContextMenu={props.unscheduled.onContextMenu}
                        onToggle={props.unscheduled.onToggle}
                        onDragStart={props.unscheduled.onDragStart}
                        onDragEnd={props.unscheduled.onDragEnd}
                    />
                </SidePanel>
            )}

            {!task && !goal && project && (
                <SidePanel>
                    <ProjectPanel
                        key={project.id || "new"}
                        value={project}
                        ancestors={fromCrumb}
                        onClose={closeTo(fromCrumb)}
                        onSaved={saved => {
                            // a new project becomes an existing one, which switches the
                            // panel over to saving as you go
                            if (!project.id) openProject(saved);
                        }}
                        onOpenTask={openTaskInside}
                        onOpenGoal={openGoalInside}
                    />
                </SidePanel>
            )}
        </>
    );

    return {
        // what's open, for the row the page draws as selected
        task: task,
        project: project,
        goal: goal,

        openTask: openTask,
        openProject: openProject,
        openGoal: openGoal,
        close: closeAll,

        // unscheduledOpen is true while the unscheduled list is in the panel
        unscheduledOpen: unscheduledOpen && !task && !project && !goal,
        toggleUnscheduled: toggleUnscheduled,
        openUnscheduled: openUnscheduled,

        element: element,
        LoadingOrErrorElement: (
            <>
                {fetchProject.LoadingOrErrorElement}
                {fetchGoal.LoadingOrErrorElement}
                {fetchFrom.LoadingOrErrorElement}
            </>
        ),
    };
}
